/**
 * Getting the 402 KB Hanzi→reading table to the worker, once, and only when a
 * page actually needs it.
 *
 * ── WHY THIS IS A FETCH AND NOT AN IMPORT ─────────────────────────────────
 * `src/pinyin/table.json` is 402 KB — larger than the entire page bundle. An
 * `import` would put it in `boot.js` for every visitor of every page, so a
 * person converting an SRT would download a Chinese pronunciation dictionary to
 * do it. `adapter.md` §9 says "lazy-loaded on first PinyinOp, never statically
 * bundled", and this is that sentence implemented.
 *
 * ── WHY IT IS SENT RATHER THAN RETURNED ───────────────────────────────────
 * The table crosses as the fire-and-forget `pinyin` message. That is safe
 * because `MessagePort` delivery is ORDERED: a table posted before the op
 * arrives before the op, so there is no handshake to get wrong and no ready
 * flag to race. `runPipeline` rejects a `PinyinOp` with
 * `'invalid-op-parameters'` naming the missing table when it is absent, so a
 * page that forgets this fails with a message saying what to load.
 *
 * ── WHAT IT DELIBERATELY DOES NOT DO ──────────────────────────────────────
 * It does not swallow a failure. A pinyin tool whose table did not load and
 * which quietly returns the input looks like it worked, and `adapter.md` freezes
 * that as the rule: the op never passes text through unchanged. So a failed load
 * rejects, the caller shows an error, and nothing produces a file.
 */

import type { PinyinMessage } from '../../../../../src/core/protocol.ts';
import { type CompactTable, expandEntry } from '../../../../../src/pinyin/table.ts';
import { qs } from './dom.ts';

type PinyinTable = PinyinMessage['table'];

/**
 * Where the table is served from.
 *
 * ⚠ NOT under `/static/build/`. `build/build.mjs` does `rmSync(outdir)` on every
 * run, so anything placed there by a step other than esbuild disappears the next
 * time somebody builds — silently, and only for whoever built last. `data/` is
 * outside that blast radius.
 *
 * Cache-busted with the same `build_id` as every other asset: the table changes
 * only when the source data does, but it is served with a long max-age and a
 * stale dictionary produces wrong readings rather than a visible failure.
 */
const TABLE_PATH = 'data/pinyin-table.json';

/**
 * The URL, and it does not require a DOM to compute one.
 *
 * `document` is guarded rather than assumed because this module is a plain URL
 * derivation plus a fetch — nothing in it is inherently main-thread — and a
 * module that throws `ReferenceError: document is not defined` when its only
 * DOM use is an optional cache-buster has made itself untestable for no reason.
 * Without the meta tag the URL is still correct, just uncached-busted.
 */
function tableUrl(): string {
  const buildId =
    typeof document === 'undefined'
      ? ''
      : (qs<HTMLMetaElement>('meta[name="build-id"]')?.content.trim() ?? '');
  return `/static/${TABLE_PATH}${buildId ? `?v=${encodeURIComponent(buildId)}` : ''}`;
}

/**
 * One in-flight fetch per page, shared by every caller.
 *
 * The promise is cached rather than the table: two option changes in the same
 * second would otherwise start two 402 KB downloads, and caching only the
 * resolved value does not help because the race is before resolution.
 */
let inflight: Promise<PinyinTable> | null = null;

export function loadPinyinTable(fetchImpl: typeof fetch = fetch): Promise<PinyinTable> {
  inflight ??= fetchImpl(tableUrl(), { credentials: 'omit' })
    .then(async (res) => {
      if (!res.ok) throw new Error(`pinyin table: HTTP ${res.status}`);
      const table = (await res.json()) as unknown;
      if (!isCompactTable(table)) throw new Error('pinyin table: not a Hanzi→readings map');
      return expand(table);
    })
    .catch((err: unknown) => {
      // Clear the cache so a retry is possible. A page that failed once because
      // the user was on a train should not be permanently unable to run the
      // tool for the rest of the session.
      inflight = null;
      throw err;
    });
  return inflight;
}

/**
 * A shape check, not a full validation.
 *
 * It exists because the failure it catches is specific and silent: an HTML error
 * page served with a 200 by a misconfigured proxy parses as JSON only sometimes,
 * and an empty object parses fine and produces a document where every character
 * is left as-is — which is exactly the "looks like it worked" outcome the
 * contract forbids.
 *
 * THE SERVED FILE IS THE COMPACT FORM, character → comma-joined readings, which
 * is what `src/pinyin/table.json` stores and what `build/build.mjs` emits. This
 * check used to expect the expanded wire shape, so the moment the build started
 * serving the real file every load was rejected as "not a Hanzi→readings map":
 * the two halves were each tested against their own fixture and never against
 * each other. test/runner/assets.test.mjs now feeds the built file through here.
 */
function isCompactTable(value: unknown): value is CompactTable {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const entries = Object.entries(value as Record<string, unknown>);
  if (entries.length === 0) return false;
  const [, readings] = entries[0] as [string, unknown];
  return typeof readings === 'string' && readings !== '';
}

/**
 * Compact → the `pinyin` message's `{text, tone, sourceLength}[]` shape, once per
 * page. `expandEntry` is the stored form's own expander, imported rather than
 * re-written, so the tone rule cannot fork between the table and this page.
 */
function expand(table: CompactTable): PinyinTable {
  const out: Record<string, PinyinTable[string]> = {};
  for (const [hanzi, readings] of Object.entries(table)) out[hanzi] = expandEntry(readings);
  return out;
}

/** For tests, and for a page that navigates away mid-load. */
export function resetPinyinTable(): void {
  inflight = null;
}
